/**
 * Agreement Prompt Component
 *
 * Displays when admin users need to sign the PandaDoc BAA agreement.
 * Opens PandaDoc in external window.
 *
 * CHROME EXTENSION REQUIRED:
 * - Extension intercepts PandaDoc navigation
 * - Opens in new tab (keeps web app alive)
 * - Monitors for BAA signing completion
 * - Notifies web app immediately
 * - Auto-closes PandaDoc tab
 */

import React, { useCallback } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:agreement-prompt');
import { Button } from './button';
import { Card } from './card';
import { cn } from '../utils/cn';
import { useBAASigning } from '../hooks/use-baa-signing';
import { usePandaDocMonitor } from '../hooks/use-pandadoc-monitor';
import { usePlatform } from '../contexts/platform-context';
import { toast } from '../utils/toast';
import BlueShieldSvg from '../assets/icons/blue-shield.svg';
import GreenCheckSvg from '../assets/icons/green-check.svg';

interface AgreementPromptProps {
  onClose?: () => void;
  isLoading?: boolean;
  className?: string;
}

export const AgreementPrompt: React.FC<AgreementPromptProps> = ({
  isLoading = false,
  className,
}) => {
  const { isSidebar, webAppUrl } = usePlatform();

  // BAA signing functionality - toasts handled by hook
  const { openBAASession, isLoading: isBAALoading } = useBAASigning({
    viewIfSigned: false, // Don't allow viewing from this component - must sign
    showToasts: true, // Show toasts for user feedback
  });

  // Handler for "Open in Web App" button
  const handleOpenInWebApp = useCallback(() => {
    logger.info('📱 [AGREEMENT-PROMPT] Opening BAA signing in full web app');
    window.open(webAppUrl, '_blank');
  }, [webAppUrl]);

  // Listen for BAA signing success from extension (for additional UI feedback)
  React.useEffect(() => {
    const handleExtensionSuccess = (event: MessageEvent) => {
      // Only accept messages from our window
      if (event.source !== window) return;

      const message = event.data;
      if (message.type === 'PANDADOC_SUCCESS') {
        logger.info('🎉 [AGREEMENT-PROMPT] BAA signing success from extension!', {
          sessionId: message.sessionId,
        });

        // Show additional success toast (useBAASign hook also shows one)
        toast.success(
          'BAA Signed Successfully! 🎉',
          'Your agreement has been recorded. The page will refresh shortly...'
        );
      }
    };

    window.addEventListener('message', handleExtensionSuccess);

    return () => {
      window.removeEventListener('message', handleExtensionSuccess);
    };
  }, []);

  return (
    <div className={cn('flex flex-col items-center justify-center', className)}>
      {/* Shield Icon at the top */}
      <div className="mb-6 mt-12">
        <img src={BlueShieldSvg} alt="Security Shield" className="w-32 h-32" />
      </div>

      {/* Main Title */}
      <h1 className="text-xl font-bold text-foreground mb-4">BAA Required</h1>

      <div className="max-w-[620px] flex flex-col items-center">
        {/* Description */}
        <p className="text-foreground text-center leading-relaxed">
          {isSidebar
            ? 'BAA signing requires the full web application for secure document handling'
            : 'Please sign the Business Associate Agreement (BAA) before setting up your subscription'}
        </p>

        {/* Additional info with link */}
        <p className="text-foreground mb-6 text-center">
          This agreement is required for HIPAA compliance and protects your organization. Visit{' '}
          <a
            href="https://trust.enter.health"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            trust.enter.health
          </a>{' '}
          to learn more.
        </p>

        {/* Main Card */}
        <Card className="w-full p-8 glass-card-bg glass-card-border border border-border-2">
          {/* Card Title */}
          <h3 className="text-xl font-bold text-foreground mb-4 text-left">
            Before accessing subscription features:
          </h3>

          {/* Checklist */}
          <ul className="space-y-3 mb-6">
            <li className="flex items-center text-foreground">
              <img src={GreenCheckSvg} alt="Check" className="w-5 h-5 mr-2 flex-shrink-0" />
              Review and sign the BAA
            </li>
            <li className="flex items-center text-foreground">
              <img src={GreenCheckSvg} alt="Check" className="w-5 h-5 mr-2 flex-shrink-0" />
              Agreement will open in your browser
            </li>
            <li className="flex items-center text-foreground">
              <img src={GreenCheckSvg} alt="Check" className="w-5 h-5 mr-2 flex-shrink-0" />
              Return here after signing to continue
            </li>
          </ul>

          {/* Sign BAA Button (or Open in Web App if in sidebar) */}
          <div className="flex justify-center">
            {isSidebar ? (
              <Button onClick={handleOpenInWebApp} variant="primary" size="lg">
                Open in Web App
              </Button>
            ) : (
              <Button
                onClick={openBAASession}
                disabled={isLoading || isBAALoading}
                variant="primary"
                size="lg"
              >
                {isLoading || isBAALoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    {isBAALoading ? 'Preparing BAA...' : 'Opening...'}
                  </>
                ) : (
                  'Sign BAA'
                )}
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* Footer text under card */}
      <p className="text-xs text-bg-neutral-6 mt-8 text-center max-w-md">
        Secure document signing powered by PandaDoc.
        <br />
        This agreement is required for all administrators.
      </p>
    </div>
  );
};

AgreementPrompt.displayName = 'AgreementPrompt';
